// ARCA-591 — ExtendModal (re-empower an already-empowered piece).
//
// Sibling of EmpowerModal (./EmpowerModal). Where EmpowerModal picks a role for
// a blank piece, this modal re-empowers (extends the role lifetime of) a piece
// that is ALREADY empowered — the role is fixed, so there is no 2×2 role grid.
// It surfaces the two things ARCA-591 asks for at the point of action: how many
// turns of empowerment remain, and the option to extend it.
//
// DEC-023 re-empower (PWR-05): a flat 1 token per +`extensionTurns` turns
// (default 3), capped at `maxExtensions` extensions per piece. The server is
// authoritative — `validateExtend` (apps/backend/src/match/validation/chess.ts)
// re-derives the cost and re-checks the cap (DEC-021); this modal renders the
// terms from `PlayerView.extension_terms` for DISPLAY only and gates its CTA on
// the server-computed `can_extend` flag (the ceiling) plus the token balance.
// Unlike empower, extend needs NO word this turn.
//
// Styling mirrors EmpowerModal (Dialog primitive: scrim/focus-trap/dismissal in
// the primitive; yellow ribbon; full-width primary CTA). Visible strings flow
// through react-intl per the POLISH-12 i18n coverage gate.

import type { JSX } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

import { playSfx } from '../../game/audio/useAudio'
import { Btn } from '../components/Btn'
import { Dialog } from '../components/Dialog'
import { PieceGlyph } from '../components/Icon'

/** The four empowerable roles (king is the start-piece, never re-empowerable). */
export type EmpowerableRole = 'knight' | 'bishop' | 'rook' | 'queen'

/** Re-empower deal terms — mirrors the backend wire shape `PlayerView.extension_terms`. */
export interface ExtensionTerms {
  readonly token_cost: number
  readonly turns: number
}

export interface ExtendModalProps {
  readonly onClose: () => void
  /** Confirm the re-empower of the (fixed) target piece. */
  readonly onConfirm: () => void
  /** The piece's current empowered role — drives the glyph + name. */
  readonly role: EmpowerableRole
  /** Turns of empowerment remaining: max(0, role_expires_turn − current_turn_idx). */
  readonly turnsLeft: number
  /**
   * Per-extension terms from the backend balance config
   * (`PlayerView.extension_terms`). Never hardcoded — the server stays
   * authoritative, so this is display only and cannot be spoofed.
   */
  readonly terms: ExtensionTerms
  /**
   * Server-computed extend eligibility (`board[].can_extend`): false once the
   * per-piece extension ceiling is reached. Drives the disabled "max reached"
   * state instead of a dead CTA.
   */
  readonly canExtend: boolean
  /** Caller's current token bank — gates affordability (display only). */
  readonly tokenBalance: number
}

export function ExtendModal({
  onClose,
  onConfirm,
  role,
  turnsLeft,
  terms,
  canExtend,
  tokenBalance,
}: ExtendModalProps): JSX.Element {
  const intl = useIntl()

  const affordable = tokenBalance >= terms.token_cost
  const confirmDisabled = !canExtend || !affordable

  // Ribbon: the live deal terms when extension is still possible; the ceiling
  // message when it is not (so the affordance never reads as a dead tap).
  const ribbonText = canExtend
    ? intl.formatMessage(
        { id: 'match.extend.terms' },
        { turns: terms.turns, cost: terms.token_cost },
      )
    : intl.formatMessage({ id: 'match.extend.max_reached' })

  // A note under the CTA explains the *affordability* block (the ceiling block
  // already shows in the ribbon). Mutually exclusive with the ribbon message.
  const disabledNote =
    canExtend && !affordable
      ? intl.formatMessage({ id: 'match.extend.insufficient_tokens' })
      : null

  return (
    <Dialog
      onClose={onClose}
      labelledBy="extend-header"
      panelStyle={{ padding: 0, overflow: 'hidden' }}
    >
      <div data-testid="extend-modal">
        <div
          className="card-header"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 'var(--space-3) var(--space-4)',
            background: 'var(--color-surface-deep)',
            borderBottom: '3px solid var(--color-surface-stroke)',
          }}
        >
          <span
            className="fd"
            id="extend-header"
            style={{
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-16)',
              color: 'var(--color-text-primary)',
            }}
          >
            <FormattedMessage id="match.extend.modal.title" />
          </span>
        </div>

        <div
          style={{
            padding: 'var(--space-3) var(--space-4) var(--space-4)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-3)',
            alignItems: 'center',
          }}
        >
          <div
            className="ribbon yellow"
            data-testid="extend-ribbon"
            style={{
              alignSelf: 'center',
              padding: 'var(--space-1) var(--space-4)',
              background: 'var(--color-cta-primary)',
              color: 'var(--color-inkwell)',
              borderRadius: 'var(--radius-pill)',
              fontFamily: 'var(--font-family-display)',
              fontWeight: 700,
              fontSize: 'var(--font-size-13)',
              textTransform: 'uppercase',
            }}
          >
            {ribbonText}
          </div>

          {/* Current piece: glyph + role name + turns-of-empowerment remaining.
              This is the "how many turns left" the player came here to see. */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 'var(--space-2)',
              color: 'var(--color-text-primary)',
            }}
          >
            <PieceGlyph type={role} size={40} color="currentColor" />
            <span
              className="fd"
              style={{
                fontFamily: 'var(--font-family-display)',
                fontWeight: 700,
                fontSize: 'var(--font-size-14)',
              }}
            >
              <FormattedMessage id={`match.empower.role.${role}`} />
            </span>
            <span
              data-testid="extend-turns-left"
              style={{
                fontFamily: 'var(--font-family-body)',
                fontWeight: 600,
                fontSize: 'var(--font-size-13)',
                color: 'var(--color-text-secondary)',
              }}
            >
              <FormattedMessage
                id="match.extend.turns_left"
                values={{ count: turnsLeft }}
              />
            </span>
          </div>

          <p
            style={{
              fontFamily: 'var(--font-family-body)',
              fontSize: 'var(--font-size-12)',
              color: 'var(--color-text-secondary)',
              textAlign: 'center',
              lineHeight: 1.4,
              margin: 'var(--space-2) 0 0',
            }}
          >
            <FormattedMessage id="match.extend.modal.helper" />
          </p>

          <Btn
            kind="primary"
            size="lg"
            block
            disabled={confirmDisabled}
            sfx={null}
            onClick={() => {
              if (confirmDisabled) return
              playSfx('empower')
              onConfirm()
            }}
          >
            <FormattedMessage id="match.extend.cta.confirm" />
          </Btn>

          {disabledNote !== null ? (
            <span
              data-testid="extend-disabled-note"
              style={{
                fontFamily: 'var(--font-family-body)',
                fontSize: 'var(--font-size-12)',
                color: 'var(--color-text-secondary)',
                textAlign: 'center',
              }}
            >
              {disabledNote}
            </span>
          ) : null}
        </div>
      </div>
    </Dialog>
  )
}
